/**
 * Moteur de comptabilité de position - document 07.
 *
 * Deuxième source de vérité du calcul, à côté du moteur de cotation (document
 * 03). Les deux sont indépendants et le restent : ce module n'importe rien de
 * `src/moteur/`, et le moteur de cotation ignore l'existence du livre. Coter
 * propose un prix ; le livre enregistre ce qui s'est réellement fait.
 *
 * Module pur : aucune entrée-sortie, aucune référence au DOM. Il ne lit ni
 * n'écrit de mouvement, il les applique.
 *
 * RÈGLE D'OR (document 02, section 5) : le livre est toujours reconstructible
 * depuis la séquence des mouvements triés par date. Aucun état n'est conservé
 * ailleurs. Un cache d'affichage qui divergerait du rejeu a tort par
 * construction.
 */

/** Parité officielle EUR/CFA. Fixe et légale, XOF et XAF la partagent. */
export const PARITE = 655.957;

/**
 * Seuil de stock en deçà duquel on considère la position soldée.
 *
 * Sans lui, un stock résiduel de 1e-13 après une vente totale ferait diverger
 * le CMP vers l'infini : le coût résiduel, lui aussi minuscule mais pas
 * exactement proportionnel, serait divisé par presque zéro.
 */
const EPSILON = 1e-9;

export const TYPES = [
  'achat_eur',
  'vente_eur',
  'frais_service',
  'frais_operateur',
  'depot_caisse',
  'retrait_caisse',
  'ajustement',
];

/** Livre vide : l'état avant tout mouvement. */
export function livreVide() {
  return {
    caisseEur: 0,
    caisseXaf: 0,
    caisseXof: 0,
    stockEur: 0,
    coutTotalCfa: 0,
    pnlRealiseCfa: 0,
    margeParCorridor: { XAF: 0, XOF: 0 },
  };
}

/**
 * Coût moyen pondéré, en FCFA par euro.
 *
 * Toujours dérivé, jamais stocké : deux grandeurs suffisent à le porter
 * (document 07, section 4), et un troisième champ qu'il faudrait maintenir
 * synchronisé finirait par mentir.
 */
export function cmp(livre) {
  return livre.stockEur > EPSILON ? livre.coutTotalCfa / livre.stockEur : 0;
}

const CLE_CAISSE = { XAF: 'caisseXaf', XOF: 'caisseXof', EUR: 'caisseEur' };

/**
 * Applique un mouvement et rend un NOUVEAU livre.
 *
 * Sans mutation : le rejeu produit une suite d'états, et l'instantané attaché
 * à chaque mouvement doit rester celui de son époque. Muter un objet partagé
 * ferait pointer tous les instantanés sur l'état final.
 */
export function appliquer(livre, m) {
  const l = { ...livre, margeParCorridor: { ...livre.margeParCorridor } };
  const caisseCfa = CLE_CAISSE[m.corridor];
  const eur = Number(m.montantEur) || 0;
  const cfa = Number(m.montantCfa) || 0;
  const taux = Number(m.taux) || 0;
  const parite = Number(m.parite) || PARITE;

  switch (m.type) {
    case 'achat_eur': {
      // Jambe 1 : A nous remet ses euros, on décaisse au pays pour son proche.
      // Le coût dérive du taux appliqué, jamais du taux effectif : y inclure
      // les frais reviendrait à faire entrer un revenu dans le prix de revient
      // du stock, où il ne ressortirait qu'à la revente, dilué sur tout le pool.
      const coutCfa = eur * taux;
      l[caisseCfa] -= coutCfa;
      l.caisseEur += eur;
      l.stockEur += eur;
      l.coutTotalCfa += coutCfa;
      break;
    }

    case 'vente_eur': {
      // Jambe 2 : C nous paie sur place, on livre les euros en Europe.
      //
      // Le stock sort au CMP courant, calculé AVANT la vente. C'est ce qui
      // laisse le CMP inchangé pour les euros restants : seul un achat le
      // déplace (document 07, section 5.2).
      const recetteCfa = eur * taux;
      const coutSorti = eur * cmp(livre);
      const marge = recetteCfa - coutSorti;
      l[caisseCfa] += recetteCfa;
      l.caisseEur -= eur;
      l.stockEur -= eur;
      l.coutTotalCfa -= coutSorti;
      l.pnlRealiseCfa += marge;
      if (l.margeParCorridor[m.corridor] !== undefined) {
        l.margeParCorridor[m.corridor] += marge;
      }
      break;
    }

    case 'frais_service': {
      // Revenu pur : aucun effet sur le stock ni sur le CMP.
      //
      // L'encaissement se fait en euros dans le sens sortant, en CFA dans le
      // sens entrant. `montantCfa` porte toujours la contribution au résultat,
      // exprimée en CFA ; `montantEur` porte le mouvement de trésorerie quand
      // il est en euros. La conversion se fait à la PARITÉ et non au taux
      // appliqué, faute de quoi le spread serait compté deux fois - c'est le
      // raisonnement que le moteur de cotation applique déjà à `serviceFixed`.
      if (caisseCfa && m.corridor !== 'EUR') l[caisseCfa] += cfa;
      l.caisseEur += eur;
      l.pnlRealiseCfa += cfa;
      break;
    }

    case 'frais_operateur': {
      // Toujours un décaissement en CFA. En mode refacturé, le client
      // rembourse en euros : les deux jambes existent et se compensent presque,
      // au centime d'arrondi près. En mode « à ma charge », il n'y a pas de
      // jambe euro et la charge pèse en entier sur le résultat.
      //
      // En mode « déduit », AUCUN mouvement de ce type ne doit être émis : les
      // frais sortent de l'enveloppe, le destinataire touche moins, et la
      // sortie de caisse reste le brut. Ce n'est pas un coût pour nous.
      l[caisseCfa] -= cfa;
      l.caisseEur += eur;
      l.pnlRealiseCfa += eur * parite - cfa;
      break;
    }

    case 'depot_caisse':
    case 'retrait_caisse': {
      // Apport ou prélèvement de fonds propres : la trésorerie bouge, le
      // résultat non. Confondre les deux ferait passer un apport pour un gain.
      const signe = m.type === 'depot_caisse' ? 1 : -1;
      if (m.corridor === 'EUR') l.caisseEur += signe * eur;
      else l[caisseCfa] += signe * cfa;
      break;
    }

    case 'ajustement': {
      // La seule correction possible, et la seule à déclarer son résultat
      // directement plutôt que de le dériver.
      //
      // N'agit PAS sur le stock : une erreur de position se corrige par un
      // achat ou une vente compensatoire, pour que le CMP reste toujours issu
      // de flux réels. Un ajustement libre du stock permettrait de poser un
      // coût moyen arbitraire, ce qui viderait le CMP de son sens.
      l.caisseEur += eur;
      if (caisseCfa && m.corridor !== 'EUR') l[caisseCfa] += cfa;
      l.pnlRealiseCfa += Number(m.pnlCfa) || 0;
      break;
    }

    default:
      throw new Error(`Type de mouvement inconnu : ${m.type}`);
  }

  return l;
}

/**
 * Rejoue une séquence de mouvements et rend le livre final avec son journal.
 *
 * Le tri est fait ici et non laissé à l'appelant : l'ordre change le résultat
 * dès qu'un achat suit une vente, puisque le CMP dépend de l'histoire. À
 * dates égales, l'ordre de création départage, ce qui rend le rejeu
 * déterministe même quand deux mouvements portent la même date.
 */
export function reconstruire(mouvements = []) {
  const suite = mouvements
    .filter((m) => m && !m.deleted)
    .slice()
    .sort((a, b) =>
      String(a.date).localeCompare(String(b.date)) ||
      String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? '')) ||
      String(a.id ?? '').localeCompare(String(b.id ?? '')));

  let livre = livreVide();
  const journal = [];
  for (const m of suite) {
    livre = appliquer(livre, m);
    journal.push({ mouvement: m, snapshotApres: instantane(livre) });
  }
  return { livre, journal };
}

/** Instantané réduit du livre, tel que stocké sur un mouvement (document 02). */
export function instantane(livre) {
  return {
    caisseEur: livre.caisseEur,
    caisseXaf: livre.caisseXaf,
    caisseXof: livre.caisseXof,
    stockEur: livre.stockEur,
    cmp: cmp(livre),
    pnlRealiseCfa: livre.pnlRealiseCfa,
  };
}

/**
 * Marge latente : ce que vaudrait le stock revendu à `tauxRef`.
 *
 * Elle n'est PAS acquise. Elle dépend d'un taux futur et du fait que la
 * demande se manifeste ; elle mesure une exposition, pas un gain. L'interface
 * doit la distinguer nettement de la marge réalisée.
 */
export function latent(livre, tauxRef) {
  const valeurLatenteCfa = livre.stockEur * (Number(tauxRef) || 0);
  return { valeurLatenteCfa, pnlLatentCfa: valeurLatenteCfa - livre.coutTotalCfa };
}

const SEUILS_DEFAUT = { caisseEur: 0, caisseXaf: 0, caisseXof: 0, expositionEur: Infinity };

/**
 * Alertes de pilotage - document 07, section 7.
 *
 * Elles signalent, elles ne bloquent jamais : une caisse négative est
 * comptablement licite, c'est un outil de suivi et non un contrôle bancaire.
 */
export function alertes(livre, seuils = {}) {
  const s = { ...SEUILS_DEFAUT, ...seuils };
  const liste = [];

  for (const [cle, libelle, devise] of [
    ['caisseEur', 'EUR', 'EUR'],
    ['caisseXaf', 'XAF', 'XAF'],
    ['caisseXof', 'XOF', 'XOF'],
  ]) {
    if (livre[cle] < s[cle]) {
      liste.push({
        type: 'liquidite',
        caisse: libelle,
        devise,
        valeur: livre[cle],
        seuil: s[cle],
        manque: s[cle] - livre[cle],
      });
    }
  }

  if (livre.stockEur > s.expositionEur) {
    liste.push({
      type: 'exposition',
      valeur: livre.stockEur,
      seuil: s.expositionEur,
      depassement: livre.stockEur - s.expositionEur,
    });
  }

  return liste;
}
